import type { NavigationGuard, NavigationHookAfter } from 'vue-router';

import { userTracking } from '@/modules/UserTracking';
import { buildGoToRouteTrackingEvent } from '@/modules/UserTracking/trackingEvents';
import { productTour } from '@/modules/ProductTour';

import auth from '@/modules/Auth';
import apiClient from '@/modules/ApiClient';

import {
  getBenchDirectRedirectUrl,
  getDashboardsRedirectUrl,
  getPersonalizationRedirectUrl,
  getDemandUrl,
  getAgenticAiUrl,
  redirectToAdmin,
} from '@/modules/Auth/utils/redirects';

import {
  navigateToHc,
  useLogger,
} from '@/utils';

import { thnAppsUrl, adminAccountTypes } from '@/config';
import { useAccountsService } from '@/modules/Accounts/services/AccountsService';
import {
  clearCurrentUser,
  getCurrentUser,
  setCurrentUser,
} from '@/modules/User/services/UserService';
import * as userApi from '@/modules/User/api/userApi';

export const validateAccess: NavigationGuard = async (to, from, next) => {
  const { logInfo, logError } = useLogger(`routerGuard - ${to.path}`);

  logInfo('*** Route validateAccess ***');

  const { fullPath, meta } = to;

  if (!userTracking.isInitialized()) {
    userTracking.init();
    logInfo('*** Init user tracking ***');
  }

  if (!productTour.isInitialized()) {
    await productTour.init();
    logInfo('*** Init product tour ***');
  }

  const isAuthenticated = await auth.isAuthenticated();

  // If route does not require auth, continue
  if (!to.meta.requiresAuth) {
    // block login screen from authenticated users
    if (to.path === '/login' && isAuthenticated) {
      next({
        path: '/products',
        replace: true,
      });
      return;
    }

    next();
    return;
  }

  // If route is redirect to help center, store return_to in local storage
  if (to.path === '/redirect/helpcenter') {
    localStorage.setItem('hc_return_to', to.query.return_to?.toString() || 'https://support.thehotelsnetwork.com/');
  }

  const loginAsUserId = (to.params.loginAsUserId || to.query.loginAs)?.toString();

  // If user is not authenticated, redirect to login or loginAs
  if (!isAuthenticated) {
    if (loginAsUserId) {
      auth.loginAs(String(loginAsUserId), false);
    }
    logInfo('Go to login');

    auth.login();
    return;
  }

  // If route is logout, logout and return
  if (fullPath.includes('logout')) {
    await auth.logout();
    clearCurrentUser();

    auth.login({ redirectUrl: '/products' });
    return;
  }

  // Handle user details upon login
  const redirectUrl = fullPath;

  const powerLevels = to.query.powerLevels?.toString() === 'true';

  const impersonatedUserId = auth.state.impersonatedUser?.loggedAsUserId;

  const accountsService = useAccountsService();
  const hasPidRedirect = Boolean(to.query.pid?.toString());
  const needsAccountSelection = Boolean(
    meta.requiresAccountSelected && !accountsService.account?.id,
  );

  // Reuse the user already in memory when the identity is unchanged so
  // /user-history is not refetched on every navigation.
  const cachedUser = getCurrentUser();

  let userPromise: ReturnType<typeof userApi.fetchUserDetails>;

  if (loginAsUserId) {
    // Explicit identity switch: always fetch fresh details.
    logInfo('getUserDetails');
    userPromise = userApi.fetchUserDetails({
      userId: loginAsUserId,
      deps: { apiClient },
    });
  } else if (impersonatedUserId) {
    if (cachedUser?.id === impersonatedUserId) {
      userPromise = Promise.resolve(cachedUser);
    } else {
      logInfo('getUserDetails');
      userPromise = userApi.fetchUserDetails({
        userId: impersonatedUserId,
        deps: { apiClient },
      });
    }
  } else {
    const userId = auth.getUserId() ?? '';
    if (cachedUser?.id && cachedUser.id === userId) {
      userPromise = Promise.resolve(cachedUser);
    } else {
      logInfo('getUserDetails');
      userPromise = userApi.fetchUserDetails({
        userId,
        deps: { apiClient },
      });
    }
  }

  // The accounts list only needs the userId (already available from the
  // refreshed token), so fetch + select the default account in parallel
  // with the user details instead of after them.
  const accountUserId = loginAsUserId ?? impersonatedUserId ?? auth.getUserId() ?? '';
  const runParallelAccountSelection = needsAccountSelection && !hasPidRedirect;

  let accountSelectionError: unknown;

  if (runParallelAccountSelection) {
    logInfo('Route requires an account');
  }

  const accountPromise = runParallelAccountSelection
    ? accountsService.selectDefaultAccount(accountUserId).catch((error) => {
      accountSelectionError = error;
    })
    : Promise.resolve();

  const [user] = await Promise.all([userPromise, accountPromise]);

  if (loginAsUserId) {
    auth.loginAs(
      loginAsUserId,
      powerLevels,
    );
    logInfo('LoginAs');
  }

  setCurrentUser(user);

  if (hasPidRedirect) {
    await redirectToAdmin(to.query.pid!.toString());
    return;
  }

  if (needsAccountSelection) {
    if (accountSelectionError) {
      logError('SelectDefaultAccount error', accountSelectionError);
    } else {
      logInfo(`Account '${accountsService.account?.name}' selected`);
    }

    if (!accountsService.isValidAccountSelected) {
      next({
        path: '/accounts',
        query: { redirect: redirectUrl },
        replace: true,
      });
      return;
    }
  }

  // Process redirects received in route
  const { redirectTo, redirectPath } = to.params;

  if (redirectTo) {
    switch (redirectTo.toString().toLocaleLowerCase()) {
      case 'dashboards':
        window.location.href = getDashboardsRedirectUrl();
        return;
      case 'benchdirect':
        window.location.href = getBenchDirectRedirectUrl();
        return;
      case 'personalization':
        window.location.href = getPersonalizationRedirectUrl();
        return;
      case 'conversion': {
        const pid = to.query.pid?.toString() ?? '';
        const indexOfQuery = fullPath.indexOf('?');
        if (indexOfQuery === -1) {
          await redirectToAdmin(pid, redirectPath?.toString() ?? '');
          return;
        }
        await redirectToAdmin(pid, `${redirectPath}${fullPath.substring(indexOfQuery)}`);
        return;
      }
      case 'demand':
        window.location.href = getDemandUrl();
        return;
      case 'agenticai':
        window.location.href = getAgenticAiUrl(redirectPath?.toString());
        return;
      case 'latte':
        window.location.href = thnAppsUrl.latte;
        return;
      default:
        next({
          path: '/products',
          replace: true,
        });
        return;
    }
  }

  // Redirect to latte if is internal user
  if (user && user.type === 'internal') {
    if (fullPath.includes('internal')) {
      next();
      return;
    }

    next({ path: '/internal', replace: true });
    return;
  }

  if (
    // Check if route needs active properties for the user
    (meta.requiresActiveProperties
      && accountsService.account?.user?.active_properties_count <= 0)
    // Check if route is reserved for Team Member profiles only
    || (meta.forTeamMembersOnly && !user?.type?.includes('team_member'))
    // Check if route is reserved for Admins or Account Owners only
    || (meta.requiresOwnerOrAdmin
      && !adminAccountTypes.includes(accountsService.account?.team_member?.role ?? ''))
  ) {
    next({
      path: '/preferences',
      replace: true,
    });
    return;
  }
  if (loginAsUserId || to.query.code) {
    next({
      path: '/products',
      replace: true,
    });
    return;
  }

  logInfo(`*** User redirected to ${to.path} ***`);

  // If user came from a help center redirect, open help center tab
  const helpCenterRedirect = localStorage.getItem('hc_return_to');

  if (helpCenterRedirect) {
    localStorage.removeItem('hc_return_to');
    await (navigateToHc(helpCenterRedirect.replace(/^.*\/hc\/[^/]+\//, ''), false));
    return;
  }

  next();
};

export const tracking: NavigationHookAfter = (to) => {
  const { logInfo } = useLogger(`trackPage - ${to.path}`);

  logInfo('*** Route trackPage ***');

  if (to.name) {
    logInfo('*** Track user tracking action page ***');
    userTracking.addAction(buildGoToRouteTrackingEvent(String(to.name)));
    logInfo('*** Track product tour page ***');
    productTour.trackPage();
  }
};
